import { motion } from "framer-motion";
import { Check } from "lucide-react";

const PRICING_BG =
  "https://static.prod-images.emergentagent.com/jobs/186afb54-ad9f-4a49-8eb9-c7e4ff21130d/images/02eb092d5c4d3c1a031c5ef81fe802e17279bf4fb6e584efff302100c35dd9fd.png";

// === Programs base content ===
const INDIVIDUELLE_FEATURES = [
  "Travail technique adapté",
  "Préparation physique spécifique",
  "Préparation mentale",
  "Sans engagement long terme",
];

const U13_SENIOR_FEATURES = [
  "Analyse vidéo & correction technique",
  "Préparation physique spécifique",
  "Préparation mentale",
  "Observation en match & en club",
  "Suivi & communication tripartite",
];

// === Tariff variants ===
const CLASSIQUE = {
  label: "Tarif Classique",
  description:
    "Tarifs publics pour gardiens non-licenciés au sein de la structure.",
  plans: [
    {
      id: "individuelle_classique",
      label: "Séances individuelles à la carte",
      duration: "Programme 1 · 1h",
      tiers: [
        { qty: "1 séance", price: "50 €" },
        { qty: "5 séances", price: "200 €" },
        { qty: "10 séances", price: "400 €" },
      ],
      features: INDIVIDUELLE_FEATURES,
      cta: "Réserver",
      accent: false,
    },
    {
      id: "u13_senior_classique",
      label: "Programme U13 — Senior",
      duration: "Programme 2 · 4 séances terrain",
      tiers: [{ qty: "", price: "250 €" }],
      features: U13_SENIOR_FEATURES,
      cta: "Choisir ce forfait",
      accent: true,
    },
    {
      id: "n3_pro",
      label: "Programme N3 / PRO",
      duration: "Programme 3 · Haut niveau",
      tiers: [{ qty: "", price: "Sur devis" }],
      features: [
        "Prépa avant saison",
        "Préparation match",
        "Analyse vidéo post match",
        "Debrief post match",
        "Préparation mentale",
      ],
      cta: "Demander un devis",
      accent: false,
    },
  ],
};

const LICENCIE = {
  label: "Tarif Licencié",
  description:
    "Tarif préférentiel −30% pour les gardiens licenciés au sein de la structure.",
  plans: [
    {
      id: "individuelle_licencie",
      label: "Séances individuelles à la carte",
      duration: "Programme 1 · 1h",
      tiers: [
        { qty: "1 séance", price: "35 €", strike: "50 €" },
        { qty: "5 séances", price: "140 €", strike: "200 €" },
        { qty: "10 séances", price: "280 €", strike: "400 €" },
      ],
      features: INDIVIDUELLE_FEATURES,
      cta: "Réserver",
      accent: false,
    },
    {
      id: "u13_senior_licencie",
      label: "Programme U13 — Senior",
      duration: "Programme 2 · 4 séances terrain",
      tiers: [{ qty: "", price: "175 €", strike: "250 €" }],
      features: U13_SENIOR_FEATURES,
      cta: "Choisir ce forfait",
      accent: true,
    },
  ],
};

function PlanCard({ p }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6 }}
      className={`relative flex flex-col bg-[#0a0a0a] border p-8 md:p-10 rounded-sm ${
        p.accent
          ? "border-[#CCFF00] shadow-[0_0_60px_-20px_#CCFF00]"
          : "border-white/10"
      }`}
      data-testid={`pricing-card-${p.id}`}
    >
      {p.accent && (
        <span className="absolute -top-3 left-8 bg-[#CCFF00] text-black text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-sm">
          Populaire
        </span>
      )}
      <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
        {p.duration}
      </p>
      <h3 className="mt-3 font-display text-3xl md:text-4xl leading-none">
        {p.label}
      </h3>

      <div className="mt-6 pb-6 border-b border-white/10 space-y-2">
        {p.tiers.map((t) => (
          <div
            key={`${p.id}-${t.qty || "single"}-${t.price}`}
            className={`flex items-baseline ${t.qty ? "justify-between" : "justify-start"} gap-3 flex-wrap`}
          >
            {t.qty && (
              <span className="text-xs uppercase tracking-wider text-zinc-500">
                {t.qty}
              </span>
            )}
            <span className="flex items-baseline gap-3">
              {t.strike && (
                <span className="text-zinc-600 line-through text-base font-display">
                  {t.strike}
                </span>
              )}
              <span
                className={`font-display text-3xl md:text-4xl leading-none ${
                  p.accent ? "text-[#CCFF00]" : "text-white"
                }`}
              >
                {t.price}
              </span>
            </span>
          </div>
        ))}
      </div>

      <ul className="mt-6 space-y-3 flex-1">
        {p.features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
            <Check
              className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                p.accent ? "text-[#CCFF00]" : "text-zinc-500"
              }`}
              strokeWidth={2}
            />
            {f}
          </li>
        ))}
      </ul>
      <a
        href="#booking"
        data-testid={`pricing-cta-${p.id}`}
        className={`mt-8 inline-flex items-center justify-center font-bold uppercase tracking-wide text-sm px-7 py-4 rounded-sm transition-colors ${
          p.accent
            ? "bg-[#CCFF00] text-black hover:bg-white"
            : "border border-white/20 text-white hover:bg-white/5"
        }`}
      >
        {p.cta}
      </a>
    </motion.div>
  );
}

function TariffBlock({ tariff }) {
  return (
    <div>
      <h3 className="font-display text-3xl md:text-4xl leading-none mb-3">
        {tariff.label}
      </h3>
      <p className="text-sm text-zinc-500 max-w-xl mb-8">{tariff.description}</p>
      <div className={`grid grid-cols-1 gap-6 ${tariff.plans.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
        {tariff.plans.map((p) => (
          <PlanCard key={p.id} p={p} />
        ))}
      </div>
    </div>
  );
}

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 md:py-32 px-6 md:px-12 overflow-hidden border-t border-white/5"
    >
      <div className="absolute inset-0">
        <img src={PRICING_BG} alt="" className="w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/90 to-[#050505]" />
      </div>

      <div className="relative max-w-7xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] max-w-4xl"
        >
          CHOISIS TON
          <br />
          <span className="text-[#CCFF00]">PROGRAMME.</span>
        </motion.h2>
        <p className="mt-6 text-zinc-400 max-w-2xl text-base md:text-lg leading-relaxed">
          Chaque joueur bénéficie d&apos;une <span className="text-[#CCFF00] font-semibold">première séance offerte</span> au
          cours de laquelle nous analyserons son niveau et lui fournirons un
          bilan complet de sa situation actuelle. Tarifs dégressifs pour les
          clubs.
        </p>

        <div className="mt-14 space-y-20">
          <TariffBlock tariff={CLASSIQUE} />
          <TariffBlock tariff={LICENCIE} />
        </div>
      </div>
    </section>
  );
}
